import type { MouseEvent } from 'react'
import { Button } from '@/React/Shared/Component/Button'
import type { PaginatedResultsType } from '@/Shared/AutoGenerated/Main'
import { RequestQueryKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

export interface PaginationProps {
    searchResults: PaginatedResultsType
    as?: 'a' | 'button'
    url?: string
    onClick?: (payload: { event: MouseEvent; page: number }) => void
    className?: string
}

export function Pagination({
    searchResults,
    as = 'a',
    url = '',
    onClick,
    className = '',
}: PaginationProps) {
    if (!searchResults || searchResults.numberOfPages <= 1) {
        return null
    }

    const separator = url.includes('?') ? '&' : '?'

    const handlePageClick = (event: MouseEvent, page: number) => {
        if (as === 'a') {
            return
        }

        onClick?.({
            event,
            page,
        })
    }

    return (
        <nav
            role="navigation"
            aria-label="Pagination"
            className={`flex flex-wrap justify-center gap-x-1 gap-y-2 my-8 ${className}`.trim()}
        >
            {/* Before current */}
            {searchResults.beforeCurrent.map((page) => (
                <Button
                    key={page}
                    as={as}
                    onClick={(e: MouseEvent) => handlePageClick(e, page)}
                    href={
                        as === 'a'
                            ? `${url}${separator}${RequestQueryKeyEnum.PaginationPage}=${page}`
                            : undefined
                    }
                    color="light"
                    aria-label={`Page ${page}`}
                    data-page={page}
                >
                    {page}
                </Button>
            ))}

            {/* Current */}
            <Button
                as={as}
                onClick={(e: MouseEvent) =>
                    handlePageClick(e, searchResults.currentPage)
                }
                href={
                    as === 'a'
                        ? `${url}${separator}${RequestQueryKeyEnum.PaginationPage}=${searchResults.currentPage}`
                        : undefined
                }
                color="dark"
                aria-current="page"
                aria-label={`Page ${searchResults.currentPage}, current page`}
                data-page={searchResults.currentPage}
            >
                {searchResults.currentPage}
            </Button>

            {/* After current */}
            {searchResults.afterCurrent.map((page) => (
                <Button
                    key={page}
                    as={as}
                    onClick={(e: MouseEvent) => handlePageClick(e, page)}
                    href={
                        as === 'a'
                            ? `${url}${separator}${RequestQueryKeyEnum.PaginationPage}=${page}`
                            : undefined
                    }
                    color="light"
                    aria-label={`Page ${page}`}
                    data-page={page}
                >
                    {page}
                </Button>
            ))}
        </nav>
    )
}
